/**
 * 小哨與 AI（僅 admin）：接上 llama.cpp 等 OpenAI 相容的模型伺服器，並調整監控門檻。
 *
 * API：
 *   GET  /api/v1/assistant/settings          — 目前設定（金鑰只回是否已設定與末四碼）
 *   PUT  /api/v1/assistant/settings          — 儲存；api_key 留空代表沿用
 *   POST /api/v1/assistant/settings/models   — 讀模型清單（可用還沒儲存的網址與金鑰）
 *   POST /api/v1/assistant/settings/test     — 逐項測試連線、模型、回覆與工具呼叫
 *   POST /api/v1/assistant/digest/preview    — 用已儲存的設定預覽今天的全站值班摘要，不保存也不送出
 *
 * 下方的「AI 進階功能」（照片描述、聲音轉文字、語意搜尋、趨勢預警）是獨立的卡片，各自儲存，見 AiServicesCard。
 */
import { useEffect, useLayoutEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleDashed, Eye, ListRestart, Loader2, PlugZap, ShieldCheck, XCircle } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { plainReply } from '@/lib/assistant';
import { order } from '@/lib/utils';
import { fmtTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SettingSwitch } from '@/components/ui/setting-switch';
import { AiServicesCard } from '@/components/AiServicesCard';
import type { AssistantSettings, DigestPreview, ProbeResult } from '@/types/assistant';

type Form = Omit<AssistantSettings, 'api_key_set' | 'api_key_hint' | 'updated_at'>;

const NUMBER_FIELDS: { key: keyof Form; label: string; min: number; max: number; step?: number; unit: string; hint: string; group: 'advanced' | 'monitor' }[] = [
  { key: 'timeout_sec', label: '等待回覆上限', min: 5, max: 300, unit: '秒', hint: '超過就中止這一輪，避免畫面一直轉圈。', group: 'advanced' },
  { key: 'max_tokens', label: '回覆長度上限', min: 128, max: 16000, unit: 'tokens', hint: '一般問答 2000 到 4000 就夠用。', group: 'advanced' },
  { key: 'temperature', label: '溫度', min: 0, max: 2, step: 0.1, unit: '', hint: '越低越穩定；查資料建議 0.2 到 0.4。', group: 'advanced' },
  { key: 'offline_after_min', label: '離線多久算異常', min: 1, max: 1440, unit: '分鐘', hint: '離線超過這個時間的 6 倍（至少 1 小時）仍未恢復，會升為「嚴重」。', group: 'monitor' },
  { key: 'storage_low_pct', label: 'SD 卡剩餘低於', min: 1, max: 50, step: 0.5, unit: '%', hint: '回升超過門檻 5 個百分點才算恢復，不會反覆通知。', group: 'monitor' },
  { key: 'upload_fail_count', label: '一小時內上傳失敗達', min: 1, max: 100, unit: '個', hint: '同一台裝置累積到這個數量才通知。', group: 'monitor' },
];

function toForm(s: AssistantSettings): Form {
  return {
    enabled: s.enabled, base_url: s.base_url ?? '', model: s.model ?? '', timeout_sec: s.timeout_sec,
    max_tokens: s.max_tokens, temperature: s.temperature, disable_thinking: s.disable_thinking,
    offline_after_min: s.offline_after_min, storage_low_pct: s.storage_low_pct, upload_fail_count: s.upload_fail_count,
    digest_enabled: s.digest_enabled ?? true, digest_hour: s.digest_hour ?? 8, digest_use_llm: s.digest_use_llm ?? true,
  };
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);

/**
 * 儲存列貼在畫面底部時，小哨的收合圖示要停在它上方，不然手機上會蓋住「上次更新」。
 * 沿用批次操作列的 --companion-bulk-height：數值是儲存列離底部的距離加上自身高度。
 */
function useCompanionClearance() {
  const [bar, setBar] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!bar) return;
    const root = document.documentElement;
    // 手機要讓出底部導覽，離底距離和桌機不同，所以換尺寸時重算
    const update = () => {
      const offset = parseFloat(getComputedStyle(bar).bottom) || 0;
      root.style.setProperty('--companion-bulk-height', `${Math.round(offset + bar.getBoundingClientRect().height)}px`);
    };
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    window.addEventListener('resize', update);
    update();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      root.style.removeProperty('--companion-bulk-height');
    };
  }, [bar]);
  return setBar;
}

export default function AssistantSettingsPage() {
  const qc = useQueryClient();
  const saveBarRef = useCompanionClearance();
  const settings = useQuery({
    queryKey: ['assistant', 'settings'],
    queryFn: () => apiGet<AssistantSettings>('/api/v1/assistant/settings'),
  });
  const [form, setForm] = useState<Form | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [clearKey, setClearKey] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [modelsError, setModelsError] = useState('');
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [preview, setPreview] = useState<DigestPreview | null>(null);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (settings.data && typeof settings.data.enabled === 'boolean' && form === null) setForm(toForm(settings.data));
  }, [settings.data, form]);

  const save = useMutation({
    mutationFn: (body: Form & { api_key: string; clear_api_key: boolean }) =>
      apiPut<AssistantSettings>('/api/v1/assistant/settings', body),
    onMutate: () => { setError(''); setSaved(''); },
    onSuccess: (next) => {
      qc.setQueryData(['assistant', 'settings'], next);
      void qc.invalidateQueries({ queryKey: ['assistant', 'status'] });
      setForm(toForm(next));
      setApiKey('');
      setClearKey(false);
      setSaved('已儲存。小哨下一次對話就會用新的設定。');
    },
    onError: (e) => setError(e instanceof Error ? e.message : '儲存失敗'),
  });
  const listModels = useMutation({
    mutationFn: () => apiPost<{ models: string[] }>('/api/v1/assistant/settings/models', {
      base_url: form?.base_url ?? '', api_key: apiKey || undefined,
    }),
    onMutate: () => setModelsError(''),
    onSuccess: (r) => {
      const names = r?.models ?? [];
      setModels(names);
      if (names.length === 0) setModelsError('伺服器回了空的模型清單');
      else if (form && !form.model && names.length === 1) setForm({ ...form, model: names[0] });
    },
    onError: (e) => setModelsError(e instanceof Error ? e.message : '讀取失敗'),
  });
  const test = useMutation({
    mutationFn: () => apiPost<ProbeResult>('/api/v1/assistant/settings/test', {
      base_url: form?.base_url ?? '', model: form?.model ?? '', api_key: apiKey || undefined,
    }),
    onMutate: () => setProbe(null),
    onSuccess: (r) => { setProbe(r); if (r?.models?.length) setModels(r.models); },
    onError: (e) => setProbe({
      ok: false, models: [], model_found: null, reply: null, latency_ms: null, tools_ok: null,
      error: e instanceof Error ? e.message : '測試失敗',
    }),
  });
  const previewDigest = useMutation({
    mutationFn: () => apiPost<DigestPreview>('/api/v1/assistant/digest/preview'),
    onMutate: () => setPreview(null),
    onSuccess: (r) => setPreview(r),
    onError: (e) => setPreview({ body: '', source: 'rules', error: e instanceof Error ? e.message : '預覽失敗' }),
  });

  if (settings.isPending || (!form && !settings.isError)) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }
  if (settings.isError || !form) {
    return <p className="py-16 text-center text-[13px] text-destructive">
      設定載入失敗：{settings.error instanceof Error ? settings.error.message : '未知錯誤'}
    </p>;
  }

  const s = settings.data!;
  const set = <K extends keyof Form>(key: K, value: Form[K]) => { setForm({ ...form, [key]: value }); setSaved(''); };
  const missing = !form.base_url.trim() || !form.model.trim();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate({ ...form!, api_key: apiKey.trim(), clear_api_key: clearKey });
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start">
      <div className="min-w-0 space-y-5">
      <form onSubmit={onSubmit} className="space-y-5">
        <Card className="reveal" style={order(0)}>
          <CardHeader>
            <div>
              <CardTitle>模型連線</CardTitle>
              <CardDescription>填入 llama.cpp 或其他 OpenAI 相容伺服器。網址與模型可以先留空，伺服器準備好再回來填。</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-5 pt-4">
            <SettingSwitch label="啟用小哨對話" checked={form.enabled}
              description={missing ? '要先填好模型網址與模型名稱才能啟用。' : '關閉後，小哨只顯示系統摘要與通知，不會呼叫模型。'}
              onChange={(on) => set('enabled', on)} />

            <Field label="模型網址" htmlFor="ai-base-url"
              hint="填到 /v1 為止，例如 http://192.168.1.20:8080/v1。只接受 http 或 https。">
              <Input id="ai-base-url" value={form.base_url} inputMode="url" autoComplete="off" spellCheck={false}
                placeholder="http://伺服器位址:埠號/v1" onChange={(e) => set('base_url', e.target.value)} />
            </Field>

            <Field label="API 金鑰" htmlFor="ai-key"
              hint={s.api_key_set
                ? `已設定（結尾 ${s.api_key_hint ?? '****'}）。留空代表沿用，輸入新值會取代。金鑰加密保存，不會再顯示。`
                : '伺服器沒有設定金鑰就留空。金鑰加密保存，不會再顯示。'}>
              <Input id="ai-key" type="password" value={apiKey} autoComplete="new-password" spellCheck={false}
                placeholder={s.api_key_set ? '••••••••（沿用已保存的金鑰）' : '選填'}
                disabled={clearKey} onChange={(e) => { setApiKey(e.target.value); setSaved(''); }} />
              {s.api_key_set && (
                <label className="mt-2 inline-flex min-h-[36px] cursor-pointer items-center gap-2 text-[12.5px]">
                  <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={clearKey}
                    onChange={(e) => { setClearKey(e.target.checked); if (e.target.checked) setApiKey(''); }} />
                  儲存時清除已保存的金鑰
                </label>
              )}
            </Field>

            <Field label="模型名稱" htmlFor="ai-model"
              hint="填伺服器 /v1/models 列出的名稱。llama.cpp 的 llama-server 通常只有一個，按右邊按鈕就能帶入。">
              <div className="flex gap-2 max-sm:flex-col">
                <Input id="ai-model" value={form.model} list="ai-model-options" autoComplete="off" spellCheck={false}
                  placeholder="例如 codex-local" onChange={(e) => set('model', e.target.value)} />
                <datalist id="ai-model-options">{models.map((m) => <option key={m} value={m} />)}</datalist>
                <Button type="button" variant="secondary" className="shrink-0"
                  disabled={!form.base_url.trim() || listModels.isPending} onClick={() => listModels.mutate()}>
                  {listModels.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListRestart className="h-4 w-4" />}
                  讀取模型清單
                </Button>
              </div>
              {models.length > 0 && !modelsError && (
                <span className="mt-1.5 block text-[12px] text-muted-foreground">伺服器上有：{models.slice(0, 8).join('、')}{models.length > 8 ? ` 等 ${models.length} 個` : ''}</span>
              )}
              {modelsError && <span className="mt-1.5 block text-[12px] text-destructive" role="alert">{modelsError}</span>}
            </Field>

            <div className="rounded-md border border-border p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold">測試連線</p>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">用目前表單上的值測試，不必先儲存。</p>
                </div>
                <Button type="button" variant="secondary" disabled={!form.base_url.trim() || test.isPending}
                  onClick={() => test.mutate()}>
                  {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
                  {test.isPending ? '測試中…' : '開始測試'}
                </Button>
              </div>
              {probe && <ProbeSteps result={probe} model={form.model} />}
            </div>
          </CardContent>
        </Card>

        <Card className="reveal" style={order(1)}>
          <CardHeader>
            <div>
              <CardTitle>回覆方式</CardTitle>
              <CardDescription>不確定就維持預設值。</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <NumberGrid fields={NUMBER_FIELDS.filter((f) => f.group === 'advanced')} form={form} set={set} />
            <SettingSwitch label="關閉模型的思考模式" checked={form.disable_thinking}
              description="Qwen3 等會先「思考」的模型，關閉後回得比較快。模型不支援這個選項時不影響使用。"
              onChange={(on) => set('disable_thinking', on)} />
          </CardContent>
        </Card>

        <Card className="reveal" style={order(2)}>
          <CardHeader>
            <div>
              <CardTitle>異常監控門檻</CardTitle>
              <CardDescription>監控服務每分鐘檢查一次，判斷規則不需要模型；沒有設定 AI 也照常通知。</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <NumberGrid fields={NUMBER_FIELDS.filter((f) => f.group === 'monitor')} form={form} set={set} />
          </CardContent>
        </Card>

        <Card className="reveal" style={order(3)}>
          <CardHeader>
            <div>
              <CardTitle>每日值班摘要</CardTitle>
              <CardDescription>每天整理一次過去 24 小時的裝置、異常、事件與上傳狀況，放進每個人的通知清單。每個人可以在通知設定裡自己關閉。</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <SettingSwitch label="每天送出值班摘要" checked={form.digest_enabled}
              description="只看得到部分場域的人，收到的摘要只包含他們的場域。"
              onChange={(on) => set('digest_enabled', on)} />
            <Field label="送出時間" htmlFor="ai-digest-hour"
              hint="台灣時間。監控服務若剛好停機，會在之後 6 小時內補送；超過就略過當天。">
              <select id="ai-digest-hour" value={form.digest_hour} disabled={!form.digest_enabled}
                onChange={(e) => set('digest_hour', Number(e.target.value))}
                className="min-h-[44px] w-full max-w-[200px] rounded-sm border border-border bg-card px-3 text-sm disabled:opacity-50">
                {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </Field>
            <SettingSwitch label="用模型整理成短文" checked={form.digest_use_llm}
              description="模型沒接上、逾時，或寫出資料裡沒有的數字時，自動改用規則版的條列摘要。"
              onChange={(on) => set('digest_use_llm', on)} />
            <div className="rounded-md border border-border p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold">預覽今天的摘要</p>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">用已儲存的設定產生全站版本，不會保存，也不會通知任何人。</p>
                </div>
                <Button type="button" variant="secondary" disabled={previewDigest.isPending} onClick={() => previewDigest.mutate()}>
                  {previewDigest.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                  {previewDigest.isPending ? '產生中…' : '預覽'}
                </Button>
              </div>
              {preview && <div className="callout mt-4 border-t border-border pt-3" role="status">
                {preview.body && <p className="whitespace-pre-line text-[12.5px] leading-relaxed">{plainReply(preview.body)}</p>}
                {preview.body && <p className="mt-2 text-[11.5px] text-muted-foreground">
                  {preview.source === 'llm' ? '由模型整理' : '規則版條列'}
                </p>}
                {preview.error && <p className={`mt-2 text-[12px] leading-relaxed ${preview.body ? 'text-warn-on' : 'text-destructive'}`}>
                  {preview.body ? `改用規則版：${preview.error}` : preview.error}
                </p>}
              </div>}
            </div>
          </CardContent>
        </Card>

        <div ref={saveBarRef} className="assistant-save-bar reveal sticky bottom-[calc(76px+env(safe-area-inset-bottom))] z-20 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-5 py-3 shadow-sm md:bottom-4" style={order(4)}>
          <Button type="submit" disabled={save.isPending || (form.enabled && missing)}>
            {save.isPending ? '儲存中…' : '儲存設定'}
          </Button>
          <p className="min-w-0 flex-1 text-[12.5px]" role="status" aria-live="polite">
            {error ? <span className="text-destructive">{error}</span>
              : saved ? <span className="text-success-on">{saved}</span>
                : <span className="text-muted-foreground">{s.updated_at ? `上次更新：${fmtTime(s.updated_at)}` : '尚未設定過'}</span>}
          </p>
        </div>
      </form>
      <AiServicesCard style={order(5)} />
      </div>

      <Card className="reveal xl:sticky xl:top-[120px]" style={order(1)}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-success" />
            <CardTitle>小哨能做什麼</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-3 text-[12.5px] leading-relaxed text-muted-foreground">
          <p>小哨只能<strong className="text-foreground">查詢</strong>：裝置狀態、事件、檔案、監控異常與韌體部署紀錄，而且只看得到提問者有權限的場域。</p>
            <p>刪除檔案、取消上傳、重新開機這類動作，小哨只會<strong className="text-foreground">提出建議</strong>，列出固定的對象與數量，要使用者按「確認執行」才會動。提案 15 分鐘後失效。</p>
          <p>裝置密鑰、Wi-Fi 密碼、金鑰與網路位址不會交給模型。</p>
          <p>開啟「AI 進階功能」後，小哨也能依照片描述或逐字稿找檔案，並預估 SD 卡與記憶體的變化。描述是模型產生的，可能有誤，小哨回答時會附上檔案編號，方便打開原檔確認。</p>
          <p className="border-t border-border pt-3">llama.cpp 請用 <code className="rounded bg-secondary px-1 font-mono text-[11.5px]">llama-server --jinja</code> 啟動，模型才會呼叫工具；沒有加的話小哨只能聊天，查不到資料。</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-2 block text-[12.5px] font-semibold">{label}</label>
      {children}
      {hint && <span className="mt-1.5 block text-[11.5px] leading-relaxed text-muted-foreground">{hint}</span>}
    </div>
  );
}

function NumberGrid({ fields, form, set }: {
  fields: typeof NUMBER_FIELDS; form: Form; set: <K extends keyof Form>(key: K, value: Form[K]) => void;
}) {
  return (
    <div className="mb-2 grid gap-4 sm:grid-cols-3">
      {fields.map((f) => (
        <Field key={f.key} label={f.unit ? `${f.label}（${f.unit}）` : f.label} htmlFor={`ai-${f.key}`} hint={f.hint}>
          <Input id={`ai-${f.key}`} type="number" inputMode="decimal" min={f.min} max={f.max} step={f.step ?? 1} required
            value={String(form[f.key])} onChange={(e) => set(f.key, Number(e.target.value) as never)} />
        </Field>
      ))}
    </div>
  );
}

function ProbeSteps({ result, model }: { result: ProbeResult; model: string }) {
  const reached = result.models.length > 0 || result.model_found !== null;
  const steps: { label: string; state: boolean | null; detail?: string }[] = [
    { label: '連上伺服器並讀到模型清單', state: reached ? true : result.error ? false : null,
      detail: reached ? `共 ${result.models.length} 個模型` : undefined },
    { label: model ? `伺服器上有「${model}」` : '選擇模型', state: result.model_found },
    { label: '模型能回覆', state: result.reply !== null ? true : result.model_found && result.error ? false : null,
      detail: result.reply !== null ? `回覆「${result.reply.trim().slice(0, 30)}」，花了 ${result.latency_ms ?? '?'} 毫秒` : undefined },
    { label: '模型會呼叫工具（小哨查資料需要）', state: result.tools_ok },
  ];
  return (
    <div className="callout mt-4 border-t border-border pt-3">
      <ol className="space-y-2">
        {steps.map((step) => (
          <li key={step.label} className="flex items-start gap-2 text-[13px]">
            {step.state === true ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              : step.state === false ? <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                : <CircleDashed className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
            <span className="min-w-0">
              <span className={step.state === null ? 'text-muted-foreground' : ''}>{step.label}</span>
              {step.detail && <span className="block text-[12px] text-muted-foreground">{step.detail}</span>}
            </span>
          </li>
        ))}
      </ol>
      {result.error && <p className={`mt-3 text-[12.5px] leading-relaxed ${result.ok ? 'text-warn-on' : 'text-destructive'}`} role="alert">{result.error}</p>}
      {result.ok && result.tools_ok && <p className="mt-3 text-[12.5px] font-semibold text-success-on">全部通過。記得按「儲存設定」並開啟「啟用小哨對話」。</p>}
    </div>
  );
}
